import {EventEmitter, Injectable} from '@angular/core';
import { RestApiService } from '../../../../../shared/services/rest-api.service';
import { CacheRequestModel, RequestStorageService } from '../../../../../shared/services/request-storage.service';
import { Params } from '@angular/router';
import { ConfigurationService } from '../../configuration/configuration.service';
import { VehicleSourceConsts } from '../../../../consts/vehicle-source.consts';
import { CalculationService } from '../../calculation.service';
import { VehicleModelConsts } from '../../../../consts/vehicle-model.consts';
import { isNullOrUndefined } from 'util';
import { VehicleModelState } from '../../../../../shared/states/vehicle-model.state';
import { RequestMetricResult } from '../../../../../shared/components/models/metrics/request-metric-result.enum';
import { MetricsService, RequestMetricName } from '../../../../../shared/services/metrics.service';
import { ApplicationStateService } from '../../../../../shared/services/application-state.service';
import { VehicleDataModel } from '../../../../models/vehicle-data.model';
import { isPromise } from '../../../../utils/utils';
import {isNotNullOrUndefined} from 'codelyzer/util/isNotNullOrUndefined';
import {CodeDictionaryDetails} from './vehicle-model-state.service';
import {SortOrder} from '../../../../../shared/enums/sort-order.enum';

@Injectable({
  providedIn: 'root'
})
export class VehicleService {

  readonly SERVICE_KEY = 'VEHICLE_SERVICE';

  readonly EUROTAX = 'eurotax';
  readonly INFOEKSPERT = 'infoekspert';
  readonly VEHICLES = 'vehicles';
  readonly BRANDS = 'brands';
  readonly MODELS = 'models';
  readonly VERSIONS = 'versions';
  readonly AZTEC = 'aztec';
  readonly DOORS = 'doors';
  readonly PRODUCTION_YEARS = 'production_years';
  readonly FUEL_TYPES = 'fuel_types';
  readonly ENGINE_CAPACITIES = 'engine_capacities';

  public sourceConsts = VehicleSourceConsts;
  public config: { [fieldKey: string]: ParamConfig };
  public lastFieldBeforeVersions: string;
  public forceExactCollectedCarsAmount: boolean = false;

  // array of strings that specifies the order of gaining vehicle's parameters
  public fieldsOrder = [
    VehicleModelConsts.BRAND_CODE,
    VehicleModelConsts.PRODUCTION_YEAR,
    VehicleModelConsts.MODEL_CODE,
    VehicleModelConsts.FUEL_CODE,
    VehicleModelConsts.ENGINE_CAPACITY,
    // VehicleModelConsts.NUMBER_OF_DOORS,
    VehicleModelConsts.VEHICLE_CODE_EUROTAX,
    VehicleModelConsts.VEHICLE_CODE_INFOEKSPERT
  ];

  state: VehicleModelState;
  stateUpdateEvent: EventEmitter<void> = new EventEmitter<void>();

  collectedCars: {
    amount?: number;
    isAmountEstimated?: boolean;
  } = {};

  private readonly CLASSNAME = 'VehicleService';
  private collectedCarsAmountMock = [999, 659, 134, 55];

  constructor(private api: RestApiService,
              private reqStorageService: RequestStorageService,
              private configurationService: ConfigurationService,
              private logicService: CalculationService,
              private applicationStateService: ApplicationStateService,
              private metricsService: MetricsService) {
    this.createConfig();
  }

  get vehicleData(): VehicleDataModel {
    return this.logicService.vehicleData;
  }

  get registrationNumber(): string {
    if (isNullOrUndefined(this.vehicleData.registrationNumber)) {
      return '';
    }
    return this.vehicleData.registrationNumber;
  }

  get unknownRegistrationNumber(): boolean {
    return this.applicationStateService.state.unknownRegistrationNumber;
  }

  createConfig() {
    this.config = {
      [VehicleModelConsts.BRAND_CODE]: {
        uriParts: [this.EUROTAX, this.VEHICLES, this.BRANDS],
        dictionary: [],
        isStateParam: true
      },
      [VehicleModelConsts.PRODUCTION_YEAR]: {
        uriParts: [this.EUROTAX, this.VEHICLES, this.PRODUCTION_YEARS],
        dictionary: [],
        sortOrder: SortOrder.DESCENDING
      },
      [VehicleModelConsts.MODEL_CODE]: {
        uriParts: [this.EUROTAX, this.VEHICLES, this.MODELS],
        dictionary: [],
        isStateParam: true
      },
      [VehicleModelConsts.ENGINE_CAPACITY]: {
        uriParts: [this.EUROTAX, this.VEHICLES, this.ENGINE_CAPACITIES],
        dictionary: [],
        isStateParam: true
      },
      [VehicleModelConsts.FUEL_CODE]: {
        uriParts: [this.EUROTAX, this.VEHICLES, this.FUEL_TYPES],
        dictionary: [],
        isStateParam: true
      },
      // [VehicleModelConsts.NUMBER_OF_DOORS]: {
      //   uriParts: [this.VEHICLES, this.MODELS, this.DOORS],
      //   dictionary: []
      // },
      [VehicleModelConsts.VEHICLE_CODE_EUROTAX]: {
        uriParts: [this.EUROTAX, this.VEHICLES],
        dictionary: [],
        isVersionField: true
      },
      [VehicleModelConsts.VEHICLE_CODE_INFOEKSPERT]: {
        uriParts: [this.INFOEKSPERT, this.VEHICLES],
        dictionary: [],
        isVersionField: true
      }
    };

    this.fieldsOrder.forEach(fieldKey => {
      this.config[fieldKey].relatedFields = this.createRelatedFields(fieldKey);
    });

    let firstVersionFieldIndex = this.fieldsOrder.findIndex(item => this.config[item].isVersionField);
    this.lastFieldBeforeVersions = this.fieldsOrder[firstVersionFieldIndex - 1];
  }

  requestDictionary(fieldKey: string): any {
    let uri = this.api.prepareUri(...this.config[fieldKey].uriParts);
    let requestUri = this.api.prepareUri(this.configurationService.uiConfig.vehicleServiceEndpoint, uri);
    return {
      promise: this.invokeReq(requestUri, this.getReqParams(fieldKey)),
      dictionaryKey: fieldKey
    };
  }

  requestVehicleVersionData(code: string, source: VehicleSourceConsts): Promise<VehicleVersionData> {
    const versionPrefix = source === VehicleSourceConsts.EUROTAX ? 'eurotax' : 'infoekspert';
    const uri = `${this.configurationService.uiConfig.vehicleServiceEndpoint}${versionPrefix}/vehicles/vehicle/${code}`;

    let metricName = {
      className: this.CLASSNAME,
      methodName: 'requestVehicleVersionData'
    };
    const params = {code: code, source: source};
    this.metricsService.sendRequestMetric(<RequestMetricName>{
      ...metricName,
      type: RequestMetricResult.SEND
    }, params);

    return this.api.get(uri, {}, true)
      .then(resp => {
        const metricValue = {
          params: params,
          response: resp
        };
        this.metricsService.sendRequestMetric({
          ...metricName,
          type: RequestMetricResult.SUCCESS
        }, metricValue);
        return resp;
      })
      .catch(error => {
        this.metricsService.sendRequestMetric({
          ...metricName,
          type: RequestMetricResult.ERROR
        }, error);
      });
  }

  initState(fields: string[]): void {
    if (!isNotNullOrUndefined(this.state)) {
      this.state = new VehicleModelState();
    }

    if (isNotNullOrUndefined(this.applicationStateService.state.enginePower)) {
      this.state.enginePower = this.applicationStateService.state.enginePower;
    }

    fields.forEach(key => this.initStateProperty(key));
  }

  resetState(): void {
      this.state = new VehicleModelState();
  }

  infoExpertVersionPromise(registrationNumber): Promise<any> {
    let uri = this.api.prepareUri(...this.config[VehicleModelConsts.VEHICLE_CODE_INFOEKSPERT].uriParts);
    let requestUri = this.api.prepareUri(this.configurationService.uiConfig.vehicleServiceEndpoint, uri);

    const params = {
      registrationNumber: registrationNumber
    }
    return this.invokeReq(requestUri, params);
  }

  initStateProperty(key: string) {
    const { codeParam, nameParam } = this.getCodeDictionaryDetails(key);
    if (isNotNullOrUndefined(this.vehicleData[key])) {
      if (isPromise(this.config[key].dictionary)) { // dictionary is a promise
        this.config[key].dictionary.then(items => {
          const filtered = items.filter(item => item[codeParam] === this.vehicleData[key]);
          if (filtered.length > 0) {
            this.state[nameParam] = filtered[0][nameParam];
            this.stateUpdateEvent.emit();
          }
        });
      } else {
        const filtered = this.config[key].dictionary.filter(item => item[codeParam] === this.vehicleData[key]);
        if (filtered.length > 0) {
          this.state[nameParam] = filtered[0][nameParam];
          this.stateUpdateEvent.emit();
        }
      }
    } else {
        this.state[nameParam] = null;
    }
  }

  updateCollectedCarsAmount(fieldKey: string) {
    if (this.config[fieldKey].isVersionField || this.forceExactCollectedCarsAmount) { // return eurotax versions number for version fields
      this.setExactCollectedCarsAmount(VehicleModelConsts.VEHICLE_CODE_EUROTAX);
    } else {
      this.setEstimatedCollectedCarsAmount(fieldKey);
    }
  }

  requestInfoekspertLink4Dictionary(code: string): Promise<any> {
    const uri = `${this.configurationService.uiConfig.vehicleServiceEndpoint}link4/vehicles`;
    return this.api.get(uri, {
      infoekspertCode: code,
      year: this.vehicleData.productionYear
    }, true);
  }

  getCodeDictionaryDetails(fieldKey: string): CodeDictionaryDetails {
      switch (fieldKey) {
          case VehicleModelConsts.BRAND_CODE:
              return {
                  codeParam: 'brandCode',
                  nameParam: 'brandName'
              };
          case VehicleModelConsts.MODEL_CODE:
              return {
                  codeParam: 'modelCode',
                  nameParam: 'modelName'
              };
          case VehicleModelConsts.FUEL_CODE:
              return {
                  codeParam: 'fuelCode',
                  nameParam: 'fuelName'
              };
      }
  }

  private setExactCollectedCarsAmount(versionFieldKey: string): void {
    const amount = this.config[versionFieldKey].dictionary.length;
    this.collectedCars.isAmountEstimated = false;
    this.collectedCars.amount = amount;
  }

  private setEstimatedCollectedCarsAmount(fieldKey: string): void {
    const fieldIndex = this.fieldsOrder.indexOf(fieldKey);
    const amount = this.collectedCarsAmountMock[fieldIndex - 1];
    this.collectedCars.isAmountEstimated = true;
    this.collectedCars.amount = amount;
  }

  private invokeReq(requestUri: string, params?: Params): Promise<any> {
    let requestUriWithParams = requestUri + JSON.stringify(params);

    let metricName = {
      className: this.CLASSNAME,
      methodName: 'invokeReq'
    };

    if (!this.reqStorageService.isRequest(this.SERVICE_KEY, requestUriWithParams)) {
      return this.api.get(requestUri, params, true)
        .then(resp => {
          this.reqStorageService.putRequest(this.SERVICE_KEY, new CacheRequestModel(requestUriWithParams, resp));
          return resp;
        })
        .catch(error => {
          this.metricsService.sendRequestMetric({
            ...metricName,
            type: RequestMetricResult.ERROR
          }, error);
          return Promise.reject(error);
        });
    } else {
      return this.reqStorageService.getRequest(this.SERVICE_KEY, requestUriWithParams);
    }
  }

  private getReqParams(fieldKey?: string): any {
    // add registrationNumber param for vehicle versions request
    if (fieldKey === VehicleModelConsts.VEHICLE_CODE_INFOEKSPERT) {
      return {
        eurotaxCode: this.vehicleData.vehicleCodeEurotax,
        year: this.vehicleData.productionYear
      }
    }
    let params = {};
    const fieldIndex = this.fieldsOrder.indexOf(fieldKey);
    if (fieldIndex > 0) {
      let firstVersionFieldIndex = this.fieldsOrder.findIndex(item => this.config[item].isVersionField);
      let sliceEnd = this.config[fieldKey].isVersionField ? firstVersionFieldIndex : fieldIndex;
      const prevFields = this.fieldsOrder.slice(0, sliceEnd);
      prevFields.forEach(paramName => {
        params[paramName] = this.vehicleData[paramName];
      });
    }
    return params;
  }

  private getSourceForVersionField(fieldKey: string): string {
    switch (fieldKey) {
      case VehicleModelConsts.VEHICLE_CODE_EUROTAX:
        return VehicleSourceConsts.EUROTAX;
      case VehicleModelConsts.VEHICLE_CODE_INFOEKSPERT:
        return VehicleSourceConsts.INFOEXPERT_LINK4;
    }
  }

  private createRelatedFields(fieldKey: string): string[] {
    let relatedFields = [];

    if (!this.config[fieldKey].isVersionField) { // version fields have no related fields
      const fieldIndex = this.fieldsOrder.indexOf(fieldKey);
      relatedFields = this.fieldsOrder.slice(fieldIndex + 1, this.fieldsOrder.length);
    }

    return relatedFields;
  }
}

export interface ParamConfig {
  /**
   * array of strings used to compose request uri
   */
  uriParts?: string[];

  /**
   * items for select component
   */
  dictionary: any;

  /**
   * flag that specifies if value will be send in calculation data or saved in state
   */
  isStateParam?: boolean;
  /**
   * object in which value will be saved
   */

  /**
   * array containing field keys that are related to this field
   * items array of each related field is cleared after change of this field's value
   */
  relatedFields?: string[];

  /**
   * flag that specifies if field is version type
   * version type fields have no related fields
   */
  isVersionField?: boolean;

  sortOrder?: SortOrder;
}

export interface VehicleVersionData {
  fuelName: string;
  brandName: string;
  modelName: string;
  name?: string;
  engineCapacity: number;
}

export interface InfoExpertLink4DictionaryItem {
  bodyType: string;
  code: string;
  engineCapacity: number;
  horsepower: string;
  kilowatts: string;
  kind: string;
  name: string;
  numberOfDoors: number;
}
